/**
 * Hook for dashboard session-based authentication.
 * Uses the existing magic link auth flow (cookie-based, not JWT).
 */

import type { PublicUser } from '@ox/ox-brokerage-client';
import { ResponseError } from '@ox/ox-brokerage-client';
import { publicApi } from '@ox/visits-ui/clients/mtx';
import { useCallback, useEffect, useState } from 'preact/hooks';

interface DashboardAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: PublicUser | null;
  error: string | null;
  emailSent: boolean;
  sendLoginEmail: (email: string) => Promise<void>;
}

export function useDashboardAuth(): DashboardAuthState {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<PublicUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      try {
        const publicUser = await publicApi.getCurrentSessionUserApiV1PublicAuthMeGet();
        if (!cancelled) {
          setUser(publicUser);
        }
      } catch (err: unknown) {
        if (err instanceof ResponseError && [401, 403].includes(err.response.status)) {
          // Expected — not logged in
        } else if (!cancelled) {
          setError('Unable to check session. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    checkSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const sendLoginEmail = useCallback(async (email: string) => {
    setError(null);
    try {
      await publicApi.sendAuthTokenEmailApiV1PublicAuthEmailGet({
        email,
        redirectUrl: `${window.location.origin}/dashboard`,
      });
      setEmailSent(true);
    } catch {
      setError('Failed to send login email. Please try again.');
    }
  }, []);

  return {
    isLoading,
    isAuthenticated: user !== null,
    user,
    error,
    emailSent,
    sendLoginEmail,
  };
}
